import type moment from 'moment'

import asyncAction from 'modules/core/lib/asyncAction'
import { isSuccessful } from 'modules/core/lib/asyncAction'
import { dateToString } from 'modules/core/lib/dates'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useRefreshShiftsRows } from 'modules/shift_management/hooks/refreshShiftsRows'
import { DROP_ACTIONS } from 'modules/shift_management/hooks/useDragAndDrop'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import buildAddShiftsParameters from 'modules/shift_management/lib/buildAddShiftsParameters'
import {
  trackAssignEmployeesUnassignedShiftSubmit,
  trackEditShiftSubmit,
} from 'modules/shift_management/lib/trackEvents'
import { Shift, ShiftsRow, UnassignedShift } from 'modules/shift_management/lib/types'
import shiftsCollection from 'modules/shift_management/store/api/collections/shifts'
import ShiftsApi from 'modules/shift_management/store/api/collections/shifts'
import { NO_WORK_AREA } from 'modules/shift_management/store/internal/models/Shift'

export interface MoveShiftProps {
  originShiftsRow?: ShiftsRow
  newShiftsRow: ShiftsRow
  shift?: Shift
  unassignedShift?: UnassignedShift
  date: moment.Moment
  daysByEmployee: Record<string, string[]>
  employeeId: number
}

const useMoveShift = (contractVersions: ContractVersions) => {
  const [{ view }] = useSchedule()
  const { refreshShiftsRows } = useRefreshShiftsRows(contractVersions)

  const moveShift = async ({
    originShiftsRow,
    newShiftsRow,
    shift,
    unassignedShift,
    date,
    daysByEmployee,
    employeeId,
  }: MoveShiftProps) => {
    const isDaily = view.isDaily
    const isArea = view.isByAreas
    const [locationId, workAreaId] = newShiftsRow.key.split('-')
    const formattedDate = dateToString(date, 'backend')
    const formattedTime = dateToString(date, 'ui_time')

    if (unassignedShift) {
      const result = await asyncAction(() => {
        const shiftParams = buildAddShiftsParameters({
          view,
          hasSelection: false,
          defaultAttributesByDate: {},
          name: null,
          unparsedStartTime: unassignedShift.startTime,
          unparsedEndTime: unassignedShift.endTime,
          daysByRow: daysByEmployee,
          defaultShiftId: unassignedShift.defaultShiftId,
          extraHours: unassignedShift.extraHours,
          notes: unassignedShift.notes,
          locationId: unassignedShift.locationId,
          workAreaId: unassignedShift.workAreaId,
          color: null,
          templateWeekId: null,
          state: null,
          unassignedShiftId: unassignedShift.id,
          leaveId: undefined,
        })
        return ShiftsApi.rpc('bulk_create', { shifts: shiftParams })
      })

      if (isSuccessful(result)) {
        trackAssignEmployeesUnassignedShiftSubmit(unassignedShift.counter, 1, 'drag-and-drop')
        await refreshShiftsRows([newShiftsRow], [employeeId])
      }
    } else {
      const result = await asyncAction(() => {
        return shiftsCollection.rpc('drop', {
          id: shift!.id,
          employee_id: employeeId,
          date: isDaily ? null : formattedDate,
          start_time: isDaily ? formattedTime : null,
          location_id: isArea ? Number(locationId) : null,
          work_area_id: isArea && workAreaId !== NO_WORK_AREA ? Number(workAreaId) : null,
          drop_action: DROP_ACTIONS.move,
        })
      })

      if (isSuccessful(result)) {
        await trackEditShiftSubmit({
          shifts: [shift!],
          name: shift ? shift.name : '',
          startAt: null,
          endAt: null,
          notes: null,
          extraHours: null,
          locationId: isArea ? Number(locationId) : null,
          workAreaId: isArea ? Number(workAreaId) : null,
          mixedValueNotes: 0,
          view,
          assignedToModified: false,
          originalEmployeeIncluded: false,
          totalNumberOfAssignedEmployees: 1,
          through: 'drag-and-drop',
          date: isDaily ? formattedTime : formattedDate,
        })

        // Get all shift rows that have shifts by employees affected by the edit action to update stats
        await refreshShiftsRows([originShiftsRow!, newShiftsRow], [shift!.employeeId, employeeId])
      }
    }
  }

  return { moveShift }
}

export default useMoveShift
